import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import ProjectHeader from './components/ProjectHeader';
import FilterControls from './components/FilterControls';
import ComparisonTable from './components/ComparisonTable';
import ComparisonSummary from './components/ComparisonSummary';
import WeightingSection from './components/WeightingSection';
import ScoringMethodologyModal from './components/ScoringMethodologyModal';
import ProjectSelector from './components/ProjectSelector';
import Icon from '../../components/AppIcon';

import MarkStackCompleteModal from './components/MarkStackCompleteModal';
import AddVendorSearchModal from './components/AddVendorSearchModal';
import ShareStackModal from './components/ShareStackModal';
import RemindVendorsModal from './components/RemindVendorsModal';

import { getProjectNeeds } from './utils/projectNeedsMapping';
import { quoteService } from '../../services/quoteService';
import { vendorService } from '../../services/vendorService';
import { projectService } from '../../services/projectService';
import { trackEvent } from '../../hooks/useGoogleAnalytics';
import { sendStackCompleteEmail, sendVendorQuoteRequestEmail, sendVendorQuoteReminderEmail } from '../../services/emailService';

const ProjectComparison = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const projectId = searchParams?.get('project') || null;
  const [showScoringModal, setShowScoringModal] = useState(false);
  const [loadingQuotes, setLoadingQuotes] = useState(true);
  const [loadingProject, setLoadingProject] = useState(true);
  const [savingWinner, setSavingWinner] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);

  // Live project data loaded from Supabase
  const [project, setProject] = useState(null);

  // ─── Load project from Supabase on mount ──────────────────────────────────
  useEffect(() => {
    const loadProject = async () => {
      if (!projectId) {
        setLoadingProject(false);
        return;
      }
      setLoadingProject(true);
      try {
        const data = await projectService?.getById(projectId);
        if (data) {
          // Normalize to the shape used throughout the component
          setProject({
            id: data?.id,
            name: data?.name || 'Untitled Project',
            firstName: '',
            description: data?.description || '',
            category: data?.category || '',
            purchaseType: data?.specifications?.purchaseType ||
              data?.specifications?.category ||
              data?.category?.toLowerCase()?.replace(/\s+/g, '-') || '',
            icon: 'Briefcase',
            status: data?.status || 'active',
            createdDate: data?.createdAt ? data?.createdAt?.split('T')?.[0] : '',
            deadline: data?.deadline || '',
            budget: data?.budgetMax || data?.budgetMin || null,
            zipCode: data?.location || '',
            timeline: data?.specifications?.timeline || '',
            notes: data?.specifications?.notes || '',
            totalVendors: data?.vendors?.length || 0,
            receivedQuotes: data?.quotes?.filter(q => q?.status === 'submitted' || q?.status === 'accepted')?.length || 0,
            pendingQuotes: data?.quotes?.filter(q => q?.status === 'pending')?.length || 0,
            specifications: data?.specifications || {}
          });
          // Restore saved weights from Supabase
          const savedWeights = data?.specifications?.weights;
          if (savedWeights && typeof savedWeights === 'object' && Object.keys(savedWeights)?.length > 0) {
            setCustomWeights(savedWeights);
            setShowCalculations(true);
          }
          trackEvent('comparison_page_viewed', {
            project_id: projectId,
            category: data?.category || 'unknown',
          });
        }
      } catch (err) {
        console.error('Failed to load project from Supabase:', err);
        // Keep project as null — UI will show fallback
      } finally {
        setLoadingProject(false);
      }
    };
    loadProject();
  }, [projectId]);

  // Get dynamic project needs based on purchase type (derived from live project)
  const projectNeeds = getProjectNeeds(project?.purchaseType);

  const PRODUCT_CATEGORY_IDS = new Set([
    'furniture', 'mattresses', 'major-appliances', 'home-gym', 'home-security',
    'computers-laptops', 'tablets', 'smartphones', 'tv-home-theater',
    'smart-home-devices', 'audio-equipment', 'headphones', 'wearables',
    'car-purchase', 'motorcycle', 'rv', 'boat', 'tires',
    'engagement-rings', 'baby-products', 'medical-equipment'
  ]);

  const [quotes, setQuotes] = useState([]);
  const [filteredQuotes, setFilteredQuotes] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  // Track the quote id of the currently accepted/selected quote for Supabase persistence
  const [selectedQuoteId, setSelectedQuoteId] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    rating: 'all',
    sort: 'score-desc',
    minPrice: '',
    maxPrice: ''
  });
  const [sortConfig, setSortConfig] = useState({
    key: 'price',
    direction: 'asc'
  });
  const [customWeights, setCustomWeights] = useState(null);
  const [showCalculations, setShowCalculations] = useState(false);
  const [showMarkCompleteModal, setShowMarkCompleteModal] = useState(false);
  const [showAddVendorModal, setShowAddVendorModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showRemindModal, setShowRemindModal] = useState(false);
  const [isSendingReminders, setIsSendingReminders] = useState(false);

  // ─── Load quotes from Supabase on mount ───────────────────────────────────
  useEffect(() => {
    const loadQuotes = async () => {
      if (!projectId) {
        setLoadingQuotes(false);
        setQuotes([]);
        return;
      }
      setLoadingQuotes(true);
      try {
        const data = await quoteService?.getByProjectId(projectId);
        if (data && data?.length > 0) {
          // Map Supabase quote shape to the UI shape expected by ComparisonTable
          const mapped = data?.map(q => {
            // ── Detect service vs product quote ──────────────────────────────
            // Service quotes store quotesheet field responses directly in lineItems
            // (an object with keys like totalCost, timeline, warranty, laborCost, etc.)
            // Product quotes store { deliveryTime, specifications, specs, productUrl }
            const lineItems = q?.lineItems || {};
            const fieldMeta = q?.fieldMeta || null;
            const isServiceQuote = !!fieldMeta || (
              !Array.isArray(lineItems) &&
              typeof lineItems === 'object' && !('specifications' in lineItems) &&
              !('specs' in lineItems) &&
              !('deliveryTime' in lineItems) &&
              Object.keys(lineItems)?.length > 0
            );

            // ── Build specs map for scoring ───────────────────────────────────
            // For service quotes: promote all quotesheet fields into specs so
            // getVendorAssessment can find them by needId
            let specsMap = {};
            let deliveryTime = '';
            let specifications = [];

            if (isServiceQuote) {
              // All quotesheet response fields become scoreable specs
              specsMap = { ...lineItems };
              // Extract timeline for display
              deliveryTime = lineItems?.timeline || lineItems?.completionTimeline || '';
              // Build a human-readable spec list for the terms section
              // Use fieldMeta labels when available for better readability
              specifications = Object.entries(lineItems)
                ?.filter(([k, v]) => v && typeof v === 'string' && v?.trim())
                ?.map(([k, v]) => {
                  const label = fieldMeta?.[k]?.label || k;
                  return `${label}: ${v}`;
                });
            } else {
              specsMap = {
                ...(lineItems?.specs || {}),
                productUrl: lineItems?.productUrl || ''
              };
              deliveryTime = lineItems?.deliveryTime || '';
              specifications = Array.isArray(lineItems?.specifications)
                ? lineItems?.specifications
                : [];
            }

            return {
              id: q?.id,
              _supabaseId: q?.id,
              vendor: {
                id: q?.vendor?.id,
                name: q?.vendor?.name || q?.vendor?.company_name || q?.vendorName || 'Unknown Vendor',
                location: q?.vendor?.bio || '',
                email: q?.vendor?.email || '',
                phone: q?.vendor?.phone || '',
                website: q?.vendor?.website || '',
                logo: q?.vendor?.logo_url || null,
                logoAlt: `${q?.vendor?.name || 'Vendor'} logo`,
                rating: q?.vendor?.rating || null,
                reviewCount: q?.vendor?.total_reviews || null
              },
              price: q?.totalAmount || (isServiceQuote ? (parseFloat(lineItems?.totalCost) || parseFloat(lineItems?.price) || null) : null),
              deliveryTime,
              status: q?.status === 'submitted' ? 'received' : q?.status,
              submittedDate: q?.submittedAt ? q?.submittedAt?.split('T')?.[0] : '',
              validUntil: q?.validUntil || null,
              terms: q?.notes || (isServiceQuote ? (lineItems?.proposal || lineItems?.specialTerms || '') : ''),
              specifications,
              specs: specsMap,
              lineItems,
              fieldMeta,
              isServiceQuote
            };
          });
          // Only show quotes that have actually been submitted — exclude pending
          // (vendors who were invited but haven't submitted a quote yet)
          const submittedQuotes = mapped?.filter(q => q?.status === 'received' || q?.status === 'accepted');
          setQuotes(submittedQuotes);
          // Restore selectedVendor from any accepted quote
          const accepted = submittedQuotes?.find(q => q?.status === 'accepted');
          if (accepted) {
            setSelectedVendor(accepted?.vendor);
            setSelectedQuoteId(accepted?.id);
          }
        } else {
          // No quotes yet — show empty state
          setQuotes([]);
        }
      } catch (err) {
        console.error('Failed to load quotes from Supabase:', err);
        setQuotes([]);
      } finally {
        setLoadingQuotes(false);
      }
    };
    loadQuotes();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // ─── Auto-apply weights & calculate on page load ───────────────────────────
  useEffect(() => {
    // Wait until both project and quotes have finished loading
    if (loadingProject || loadingQuotes) return;
    // Need project needs to be available
    if (!projectNeeds || projectNeeds?.length === 0) return;
    // Already calculated — skip
    if (showCalculations) return;

    if (customWeights && Object.keys(customWeights)?.length > 0) {
      // Saved weights already restored from Supabase — just show calculations
      setShowCalculations(true);
    } else {
      // Apply balanced template automatically
      const even = parseFloat((100 / projectNeeds?.length)?.toFixed(2));
      const balanced = {};
      projectNeeds?.forEach((need, idx) => {
        balanced[need.id] = even;
      });
      // Fix rounding so total is exactly 100
      const total = Object.values(balanced)?.reduce((s, v) => s + v, 0);
      const diff = parseFloat((100 - total)?.toFixed(2));
      if (Math.abs(diff) > 0.001) {
        const lastId = projectNeeds?.[projectNeeds?.length - 1]?.id;
        balanced[lastId] = parseFloat((balanced?.[lastId] + diff)?.toFixed(2));
      }
      handleWeightsComplete(balanced);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingProject, loadingQuotes, projectNeeds?.length]);

  const showToast = (message, type = 'success') => {
    setToastMessage({ message, type });
    setTimeout(() => setToastMessage(null), 3500);
  };

  // ─── Show toast from navigation state (e.g. after stack creation) ─────────
  useEffect(() => {
    if (location?.state?.message) {
      showToast(location?.state?.message, location?.state?.type || 'success');
      // Clear the state so it doesn't re-show on refresh
      navigate(location?.pathname + location?.search, { replace: true, state: {} });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleWeightsComplete = (weights) => {
    setCustomWeights(weights);
    setShowCalculations(true);
    // Persist weights to Supabase inside project.specifications.weights
    if (projectId) {
      const currentSpecs = project?.specifications || {};
      projectService?.update(projectId, {
        specifications: { ...currentSpecs, weights }
      })?.catch(err => console.error('Failed to save weights:', err));
    }
  };

  // Standard sorting logic without sponsored vendor priority
  useEffect(() => {
    let filtered = [...quotes];

    // Apply search filter
    if (filters?.search) {
      filtered = filtered?.filter((quote) =>
      quote?.vendor?.name?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
      quote?.vendor?.location?.toLowerCase()?.includes(filters?.search?.toLowerCase())
      );
    }

    // Apply status filter
    if (filters?.status !== 'all') {
      filtered = filtered?.filter((quote) => quote?.status === filters?.status);
    }

    // Apply rating filter
    if (filters?.rating !== 'all') {
      const minRating = parseFloat(filters?.rating?.replace('+', ''));
      filtered = filtered?.filter((quote) => quote?.vendor?.rating >= minRating);
    }

    // Apply price range filter
    if (filters?.minPrice) {
      filtered = filtered?.filter((quote) =>
      quote?.price && quote?.price >= parseFloat(filters?.minPrice)
      );
    }
    if (filters?.maxPrice) {
      filtered = filtered?.filter((quote) =>
      quote?.price && quote?.price <= parseFloat(filters?.maxPrice)
      );
    }

    // Standard sorting without sponsored vendor priority
    const [sortKey, sortDirection] = filters?.sort?.split('-');
    filtered?.sort((a, b) => {
      let aValue, bValue;

      switch (sortKey) {
        case 'price':
          aValue = a?.price || Infinity;
          bValue = b?.price || Infinity;
          break;
        case 'rating':
          aValue = a?.vendor?.rating;
          bValue = b?.vendor?.rating;
          break;
        case 'delivery':
          aValue = a?.deliveryTime ? parseInt(a?.deliveryTime?.split('-')?.[0]) : Infinity;
          bValue = b?.deliveryTime ? parseInt(b?.deliveryTime?.split('-')?.[0]) : Infinity;
          break;
        case 'date':
          aValue = a?.submittedDate ? new Date(a.submittedDate) : new Date(0);
          bValue = b?.submittedDate ? new Date(b.submittedDate) : new Date(0);
          break;
        default:
          aValue = a?.vendor?.name;
          bValue = b?.vendor?.name;
      }

      if (sortDirection === 'desc') {
        return aValue < bValue ? 1 : -1;
      }
      return aValue > bValue ? 1 : -1;
    });

    setFilteredQuotes(filtered);
    setSortConfig({ key: sortKey, direction: sortDirection });
  }, [quotes, filters]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      status: 'all',
      rating: 'all',
      sort: 'score-desc',
      minPrice: '',
      maxPrice: ''
    });
  };

  const handleSort = (column) => {
    const direction = sortConfig?.key === column && sortConfig?.direction === 'asc' ? 'desc' : 'asc';
    setFilters((prev) => ({ ...prev, sort: `${column}-${direction}` }));
  };

  // ─── Select a quote / mark as winner ─────────────────────────────────────
  const handleSelectQuote = async (quote) => {
    setSelectedVendor(quote?.vendor);
    setSelectedQuoteId(quote?.id);
    await persistWinnerToSupabase(quote?.id);
  };

  const handleSelectVendor = async (vendor) => {
    setSelectedVendor(vendor);
    // Find the matching quote by vendor name so we can get its id
    const matchingQuote = quotes?.find(q => q?.vendor?.name === vendor?.name);
    if (matchingQuote) {
      setSelectedQuoteId(matchingQuote?.id);
      await persistWinnerToSupabase(matchingQuote?.id);
    }
  };

  // ─── Core: accept winner, reject others, update project status ───────────
  const persistWinnerToSupabase = async (winnerQuoteId) => {
    // Only persist if the quote id looks like a real Supabase UUID (not a mock integer)
    if (!winnerQuoteId || typeof winnerQuoteId === 'number') return;
    setSavingWinner(true);
    try {
      // Accept the winning quote
      await quoteService?.accept(winnerQuoteId);
      // Reject all other quotes for this project
      const otherQuotes = quotes?.filter(q => q?.id !== winnerQuoteId && typeof q?.id !== 'number');
      await Promise.all(otherQuotes?.map(q => quoteService?.reject(q?.id)));
      // Mark the project as awarded
      await projectService?.update(projectId, { status: 'awarded' });
      // Update local state so the UI reflects the new statuses immediately
      setQuotes(prev => prev?.map(q => ({
        ...q,
        status: q?.id === winnerQuoteId ? 'accepted' : (typeof q?.id !== 'number' ? 'rejected' : q?.status)
      })));
      showToast('Winner saved! Stack marked as awarded.');
    } catch (err) {
      console.error('Failed to persist winner to Supabase:', err);
      showToast('Could not save selection. Please try again.', 'error');
    } finally {
      setSavingWinner(false);
    }
  };

  const handleContactVendor = (vendor) => {
    console.log('Contact vendor:', vendor);
    alert(`Contacting ${vendor?.name} at ${vendor?.email}`);
  };

  const handleExport = () => {
    console.log('Exporting comparison data...');
    alert('Comparison data exported successfully!');
  };

  const handleSendReminders = async () => {
    // Find all pending vendors (quotes with status 'pending' or no quote yet)
    const pendingQuotes = quotes?.filter(q => q?.status === 'pending' || q?.status === 'received');
    const vendorsWithEmail = pendingQuotes?.filter(q => q?.vendor?.email);

    if (vendorsWithEmail?.length === 0) {
      showToast('No pending vendors with email addresses found.', 'error');
      return;
    }

    setShowRemindModal(true);
  };

  const handleConfirmReminders = async (selectedVendors) => {
    setIsSendingReminders(true);
    const buyerFirstName = project?.firstName || 'A buyer';
    const projectName = project?.name || 'a project';

    const results = await Promise.allSettled(
      selectedVendors?.map(vendor => {
        const quoteRequestUrl = `https://mibuy2878.builtwithrocket.new/vendor-quote-request-landing?projectId=${projectId}&vendorName=${encodeURIComponent(vendor?.name || '')}`;
        return sendVendorQuoteReminderEmail(vendor?.email, {
          vendorName: vendor?.name,
          projectName,
          buyerFirstName,
          quoteRequestUrl,
        });
      })
    );

    setIsSendingReminders(false);
    setShowRemindModal(false);

    const successCount = results?.filter(r => r?.status === 'fulfilled' && r?.value?.success)?.length;
    const failCount = results?.length - successCount;

    if (failCount === 0) {
      showToast(`Reminders sent to ${successCount} vendor(s)! ✓`);
    } else {
      showToast(`Sent ${successCount} reminder(s). ${failCount} failed.`, failCount === results?.length ? 'error' : 'success');
    }
  };

  const handleAddVendor = () => {
    setShowAddVendorModal(true);
    trackEvent('add_vendor_clicked', {
      project_id: projectId,
    });
  };

  // ─── Add online price result and persist to Supabase ─────────────────────
  const handleAddOnlinePriceToComparison = async (onlineItem) => {
    const NON_SPEC_NEEDS = new Set(['price', 'budget', 'cost', 'delivery', 'deliveryTime', 'shipping', 'reviews', 'reputation', 'vendorRating']);
    const specFields = projectNeeds
      ?.filter(need => !NON_SPEC_NEEDS?.has(need?.id))
      ?.map(need => need?.id);

    let specifications = specFields
      ?.map(fieldId => onlineItem?.[fieldId])
      ?.filter(Boolean);

    const fallbackSpecs = specifications?.length > 0
      ? specifications
      : [onlineItem?.condition, onlineItem?.deal]?.filter(Boolean);

    let specsMap = {};
    specFields?.forEach(fieldId => {
      if (onlineItem?.[fieldId] != null) {
        specsMap[fieldId] = onlineItem?.[fieldId];
      }
    });

    const price = typeof onlineItem?.price === 'number'
      ? onlineItem?.price
      : parseFloat(String(onlineItem?.price)?.replace(/[^0-9.]/g, '')) || 0;

    // Parse review data from the AI response
    const reviewScore = onlineItem?.reviewScore
      ? (typeof onlineItem?.reviewScore === 'number' ? onlineItem?.reviewScore : parseFloat(onlineItem?.reviewScore))
      : null;
    const reviewCount = onlineItem?.reviewCount
      ? (typeof onlineItem?.reviewCount === 'number' ? onlineItem?.reviewCount : parseInt(String(onlineItem?.reviewCount)?.replace(/[^0-9]/g, ''), 10))
      : null;

    // Optimistically add to local state immediately
    const tempId = Date.now();
    const newQuote = {
      id: tempId,
      vendor: {
        name: onlineItem?.store,
        location: 'Online Retailer',
        email: '',
        phone: '',
        website: onlineItem?.url || '',
        logo: null,
        logoAlt: `${onlineItem?.store} online retailer logo`,
        rating: reviewScore,
        reviewCount: reviewCount
      },
      price,
      deliveryTime: 'See website',
      status: 'received',
      submittedDate: new Date()?.toISOString()?.split('T')?.[0],
      validUntil: null,
      terms: onlineItem?.deal || (onlineItem?.condition ? `Condition: ${onlineItem?.condition}` : ''),
      specifications: fallbackSpecs,
      specs: specsMap,
      isOnlineResult: true
    };
    setQuotes(prev => [...prev, newQuote]);

    showToast(`"${onlineItem?.store}" added to comparison! Review it in the table below.`);

    // Persist to Supabase in the background
    try {
      // 1. Create or find a vendor row for this online store
      const vendorRow = await vendorService?.create({
        name: onlineItem?.store,
        email: '',
        phone: '',
        website: onlineItem?.url || '',
        categories: [project?.category || 'General'],
        bio: 'Online Retailer',
        rating: reviewScore,
        total_reviews: reviewCount
      });

      // 2. Create the quote row linked to the project + vendor
      const savedQuote = await quoteService?.create({
        projectId,
        vendorId: vendorRow?.id,
        status: 'submitted',
        totalAmount: price,
        lineItems: {
          deliveryTime: 'See website',
          specifications: fallbackSpecs,
          specs: specsMap
        },
        notes: onlineItem?.deal || (onlineItem?.condition ? `Condition: ${onlineItem?.condition}` : '')
      });

      // Replace the temp id with the real Supabase id
      setQuotes(prev => prev?.map(q =>
        q?.id === tempId
          ? { ...q, id: savedQuote?.id, _supabaseId: savedQuote?.id }
          : q
      ));
    } catch (err) {
      console.error('Failed to save online price to Supabase:', err);
      // Quote stays in local state even if Supabase write fails
    }
  };

  const handleRemoveVendor = (vendorName) => {
    setQuotes(prev => prev?.filter(q => q?.vendor?.name !== vendorName));
    if (selectedVendor?.name === vendorName) {
      setSelectedVendor(null);
      setSelectedQuoteId(null);
    }
  };

  // ─── Send quote request email to a vendor ────────────────────────────────
  const handleRequestQuoteFromVendor = async (vendorEmail, vendorName) => {
    if (!vendorEmail) return;
    const quoteRequestUrl = `https://mibuy2878.builtwithrocket.new/vendor-quote-request-landing?projectId=${projectId}&vendorName=${encodeURIComponent(vendorName || '')}`;
    try {
      await sendVendorQuoteRequestEmail(vendorEmail, {
        vendorName,
        projectName: project?.name || 'a project',
        buyerFirstName: project?.firstName || 'A buyer',
        quoteRequestUrl,
      });
    } catch (err) {
      console.error('Failed to send quote request email:', err);
    }
  };

  const handleEditProject = () => {
    navigate('/create-project-flow?edit=' + projectId);
  };

  const handleBackToProjects = () => {
    navigate('/all-projects');
  };

  // Show "Select A Stack" card when no project is selected
  if (!projectId) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-32 flex items-center justify-center min-h-[calc(100vh-8rem)]">
          <div className="flex flex-col items-center gap-8 px-4 w-full max-w-md">
            {/* Branded card */}
            <div className="w-full rounded-2xl overflow-hidden shadow-2xl border border-orange-100">
              {/* Gradient header strip */}
              <div
                className="py-8 px-8 flex flex-col items-center gap-3"
                style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B, #E0528A)' }}
              >
                {/* Icon badge */}
                <div className="w-16 h-16 rounded-2xl bg-white/20 border border-white/30 backdrop-blur-sm flex items-center justify-center shadow-lg">
                  <Icon name="Scale" size={32} color="white" />
                </div>
                <h2 className="text-2xl font-bold text-white tracking-tight">Select A Stack</h2>
                <p className="text-white/80 text-sm text-center leading-relaxed">
                  Choose a stack to compare vendor quotes side-by-side
                </p>
              </div>

              {/* Card body */}
              <div className="bg-white px-8 py-7 flex flex-col gap-5">
                <p className="text-text-secondary text-sm text-center">
                  Pick one of your existing stacks below to get started with the comparison.
                </p>
                <ProjectSelector currentProjectId={null} />
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-surface-200" />
                  <span className="text-xs text-text-secondary">or</span>
                  <div className="flex-1 h-px bg-surface-200" />
                </div>
                <button
                  onClick={() => navigate('/all-projects')}
                  className="w-full py-2.5 rounded-xl border-2 text-sm font-semibold transition-all hover:shadow-md"
                  style={{ borderColor: '#F06F6B', color: '#F06F6B' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'linear-gradient(135deg,#F58A54,#F06F6B)'; e.currentTarget.style.color = 'white'; e.currentTarget.style.borderColor = 'transparent'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#F06F6B'; e.currentTarget.style.borderColor = '#F06F6B'; }}
                >
                  View All Stacks
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Show full-page loading while project is being fetched
  if (loadingProject) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-32">
          <div className="flex items-center justify-center py-32">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-4 border-orange-400 border-t-transparent rounded-full animate-spin" />
              <p className="text-text-secondary text-sm">Loading stack...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Use live project data; fall back to a minimal placeholder if load failed
  const activeProject = project || {
    id: projectId,
    name: 'Stack',
    category: '',
    purchaseType: '',
    budget: null,
    status: 'active',
    totalVendors: 0,
    receivedQuotes: 0,
    pendingQuotes: 0
  };

  const isOnlineCategory = PRODUCT_CATEGORY_IDS?.has(activeProject?.purchaseType);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      {/* Toast notification */}
      {toastMessage && (
        <div className={`fixed top-20 right-4 z-50 px-5 py-3 rounded-xl shadow-lg text-white text-sm font-medium flex items-center gap-2 transition-all ${
          toastMessage?.type === 'error' ? 'bg-red-500' : 'bg-green-500'
        }`}>
          <Icon name={toastMessage?.type === 'error' ? 'AlertCircle' : 'CheckCircle'} size={16} color="white" />
          {toastMessage?.message}
        </div>
      )}
      {/* Saving indicator */}
      {savingWinner && (
        <div className="fixed top-20 right-4 z-50 px-5 py-3 rounded-xl shadow-lg bg-blue-500 text-white text-sm font-medium flex items-center gap-2">
          <Icon name="Loader" size={16} color="white" className="animate-spin" />
          Saving winner...
        </div>
      )}
      <main className="pt-32">
        {/* Coral/Orange Gradient Header Banner */}
        <div
          className="relative overflow-hidden py-12 px-4 sm:px-6 lg:px-8"
          style={{ background: 'linear-gradient(to right, #F58A54, #F06F6B, #E0528A)' }}
        >
          {/* Decorative blobs */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-white/5 blur-3xl" />
            <div className="absolute bottom-0 -left-10 w-48 h-48 rounded-full bg-white/5 blur-2xl" />
            {/* Decorative floating icons */}
            {[
              { name: 'BarChart3', top: '15%', left: '3%', rotate: '-10deg', opacity: 0.35 },
              { name: 'Scale', top: '55%', left: '1%', rotate: '8deg', opacity: 0.3 },
              { name: 'Star', top: '15%', right: '3%', rotate: '12deg', opacity: 0.35 },
              { name: 'DollarSign', top: '55%', right: '1%', rotate: '-9deg', opacity: 0.3 },
            ]?.map((item, i) => (
              <div
                key={i}
                className="absolute"
                style={{ top: item?.top, left: item?.left, right: item?.right, opacity: item?.opacity, transform: `rotate(${item?.rotate})` }}
              >
                <div className="w-12 h-12 rounded-2xl bg-white/20 border border-white/30 flex items-center justify-center backdrop-blur-sm">
                  <Icon name={item?.name} size={22} color="white" />
                </div>
              </div>
            ))}
          </div>

          <div className="relative max-w-7xl mx-auto">
            {/* Badge */}
            <div className="flex mb-4">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5">
                <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
                <span className="text-white/90 text-sm font-medium tracking-wide">Step 3 of 4 — Compare & Choose</span>
              </div>
            </div>

            <Breadcrumb
              projectName={activeProject?.name}
              customPath={[
                { label: 'Stacks', path: '/all-projects', isClickable: true },
                { label: activeProject?.name, path: null, isClickable: false },
                { label: 'Compare', path: '/project-comparison', isClickable: false }
              ]}
            />
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">Stack Comparison</h1>
            <p className="text-white/80 text-lg">Compare vendor quotes side-by-side to find the best option</p>

            {/* Mini step flow */}
            <div className="flex items-center gap-2 mt-6">
              {[
                { label: 'Receive', icon: 'FileCheck' },
                { label: 'Filter', icon: 'SlidersHorizontal' },
                { label: 'Decide', icon: 'CheckCircle' },
              ]?.map((step, i) => (
                <React.Fragment key={step?.label}>
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-xl ${
                    i === 1 ? 'bg-white text-primary font-semibold shadow-md' : 'bg-white/15 text-white/80'
                  }`}>
                    <Icon name={step?.icon} size={14} color={i === 1 ? '#F06F6B' : 'white'} />
                    <span className="text-xs font-medium">{step?.label}</span>
                  </div>
                  {i < 2 && <Icon name="ChevronRight" size={14} color="rgba(255,255,255,0.5)" />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Project Selection Dropdown */}
          <div className="mb-6">
            <ProjectSelector currentProjectId={projectId} />
          </div>

          <ProjectHeader
            project={activeProject}
            onSendReminders={handleSendReminders}
            onAddVendor={handleAddVendor}
            onEditProject={handleEditProject}
            onBackToProjects={handleBackToProjects}
            onMarkComplete={() => setShowMarkCompleteModal(true)}
            onShareStack={() => setShowShareModal(true)}
            isOnlineCategory={isOnlineCategory} />

          {/* Loading state */}
          {loadingQuotes ? (
            <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
              {/* Main skeleton area */}
              <div className="xl:col-span-3 space-y-6">
                {/* Filter bar skeleton */}
                <div className="bg-white rounded-2xl border border-surface-200 p-4 animate-pulse">
                  <div className="flex items-center gap-3">
                    <div className="h-9 bg-surface-200 rounded-xl flex-1" />
                    <div className="h-9 w-28 bg-surface-200 rounded-xl" />
                    <div className="h-9 w-28 bg-surface-200 rounded-xl" />
                    <div className="h-9 w-24 bg-surface-200 rounded-xl" />
                  </div>
                </div>

                {/* Weighting section skeleton */}
                <div className="bg-white rounded-2xl border border-surface-200 p-5 animate-pulse">
                  <div className="flex items-center justify-between mb-4">
                    <div className="h-5 w-40 bg-surface-200 rounded-lg" />
                    <div className="h-8 w-24 bg-surface-200 rounded-xl" />
                  </div>
                  <div className="flex gap-3">
                    {[1, 2, 3, 4]?.map(i => (
                      <div key={i} className="flex-1 h-3 bg-surface-200 rounded-full" />
                    ))}
                  </div>
                </div>

                {/* Vendor quote card skeletons */}
                {[1, 2, 3]?.map(i => (
                  <div key={i} className="bg-white rounded-2xl border border-surface-200 p-6 animate-pulse">
                    <div className="flex items-start justify-between mb-5">
                      <div className="flex items-center gap-4">
                        {/* Logo placeholder */}
                        <div className="w-12 h-12 rounded-xl bg-surface-200" />
                        <div className="space-y-2">
                          <div className="h-5 w-36 bg-surface-200 rounded-lg" />
                          <div className="h-3.5 w-24 bg-surface-200 rounded-md" />
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-20 bg-surface-200 rounded-xl" />
                        <div className="h-8 w-8 bg-surface-200 rounded-xl" />
                      </div>
                    </div>

                    {/* Price + score row */}
                    <div className="flex items-center gap-4 mb-5">
                      <div className="h-8 w-28 bg-surface-200 rounded-xl" />
                      <div className="h-6 w-20 bg-surface-200 rounded-full" />
                      <div className="h-6 w-16 bg-surface-200 rounded-full" />
                    </div>

                    {/* Spec bars */}
                    <div className="space-y-3">
                      {[1, 2, 3]?.map(j => (
                        <div key={j} className="flex items-center gap-3">
                          <div className="h-3.5 w-24 bg-surface-200 rounded-md" />
                          <div className="flex-1 h-2.5 bg-surface-200 rounded-full" />
                          <div className="h-3.5 w-10 bg-surface-200 rounded-md" />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Sidebar skeleton */}
              <div className="xl:col-span-1 space-y-4">
                <div className="bg-white rounded-2xl border border-surface-200 p-5 animate-pulse">
                  <div className="h-5 w-32 bg-surface-200 rounded-lg mb-4" />
                  <div className="space-y-3">
                    {[1, 2, 3, 4]?.map(i => (
                      <div key={i} className="flex items-center justify-between">
                        <div className="h-3.5 w-20 bg-surface-200 rounded-md" />
                        <div className="h-3.5 w-14 bg-surface-200 rounded-md" />
                      </div>
                    ))}
                  </div>
                  <div className="mt-5 h-10 bg-surface-200 rounded-xl" />
                </div>
                <div className="bg-white rounded-2xl border border-surface-200 p-5 animate-pulse">
                  <div className="h-5 w-28 bg-surface-200 rounded-lg mb-4" />
                  <div className="space-y-2">
                    {[1, 2]?.map(i => (
                      <div key={i} className="h-3.5 bg-surface-200 rounded-md" />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
              {/* Main Comparison Area - Now takes more space */}
              <div className="xl:col-span-3 space-y-6">
                <FilterControls
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onClearFilters={handleClearFilters}
                  onExport={handleExport}
                  quotesCount={filteredQuotes?.length} />

                {/* Weighting Section */}
                <WeightingSection
                  projectNeeds={projectNeeds}
                  purchaseType={activeProject?.purchaseType}
                  onWeightsComplete={handleWeightsComplete}
                  initialWeights={customWeights || {}} />

                {/* Comparison Table */}
                <ComparisonTable
                  quotes={filteredQuotes}
                  vendors={filteredQuotes?.map((q) => q?.vendor)}
                  onSort={handleSort}
                  sortConfig={sortConfig}
                  onSelectQuote={handleSelectQuote}
                  onSelectVendor={handleSelectVendor}
                  selectedVendor={selectedVendor}
                  customWeights={customWeights}
                  showCalculations={showCalculations}
                  projectNeeds={projectNeeds}
                  project={activeProject}
                  onContactVendor={handleContactVendor}
                  onRemoveVendor={handleRemoveVendor} />
              </div>

              {/* Summary Sidebar - Now narrower */}
              <div className="xl:col-span-1">
                <ComparisonSummary
                  quotes={filteredQuotes}
                  selectedCriteria={filters}
                  selectedVendor={selectedVendor}
                  onAddToComparison={handleAddOnlinePriceToComparison}
                  projectNeeds={projectNeeds}
                  project={activeProject} />

              </div>
            </div>
          )}

          {/* Empty State — No quotes at all (branded) */}
          {!loadingQuotes && quotes?.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 px-4">
              {/* Branded card */}
              <div className="bg-white rounded-3xl shadow-xl border border-orange-100 px-10 py-14 max-w-md w-full flex flex-col items-center text-center">
                {/* Decorative icon circle */}
                <div
                  className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
                  style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}
                >
                  <Icon name="Layers" size={36} color="white" />
                </div>

                {/* Heading */}
                <h3 className="text-2xl font-bold text-foreground mb-3">No Active Stacks</h3>

                {/* Supporting message */}
                <p className="text-text-secondary text-base leading-relaxed mb-6">
                  You haven't created any stacks yet. Build your first stack to start comparing vendor quotes side-by-side.
                </p>

                {/* CTA */}
                <button
                  onClick={() => navigate('/create-project-flow')}
                  className="w-full py-3 px-6 rounded-xl text-white font-semibold text-base transition-all hover:opacity-90 active:scale-95 shadow-md"
                  style={{ background: 'linear-gradient(to right, #F58A54, #F06F6B)' }}
                >
                  Create a Stack
                </button>

                {/* Secondary link */}
                <button
                  onClick={() => navigate('/all-projects')}
                  className="mt-4 text-sm font-medium transition-colors hover:underline"
                  style={{ color: '#F58A54' }}
                >
                  View All Stacks
                </button>
              </div>
            </div>
          )}

          {/* Empty State — Quotes exist but filters hide them */}
          {!loadingQuotes && quotes?.length > 0 && filteredQuotes?.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 px-4">
              <div className="bg-white rounded-3xl shadow-lg border border-orange-100 px-10 py-12 max-w-md w-full flex flex-col items-center text-center">
                {/* Icon */}
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mb-5"
                  style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}
                >
                  <Icon name="SearchX" size={28} color="white" />
                </div>

                <h3 className="text-xl font-bold text-foreground mb-2">No Vendors Match</h3>
                <p className="text-text-secondary text-sm leading-relaxed mb-6">
                  None of the vendors in this stack match your current filter criteria. Try adjusting your search, price range, or status filters to see more results.
                </p>

                {/* Active filter hints */}
                <div className="w-full bg-orange-50 rounded-xl px-4 py-3 mb-6 text-left">
                  <p className="text-xs font-semibold text-orange-700 mb-2 uppercase tracking-wide">Active Filters</p>
                  <div className="flex flex-wrap gap-2">
                    {filters?.search && (
                      <span className="inline-flex items-center gap-1 bg-white border border-orange-200 text-orange-700 text-xs font-medium px-2.5 py-1 rounded-full">
                        <Icon name="Search" size={10} color="#c2410c" />
                        "{filters?.search}"
                      </span>
                    )}
                    {filters?.status !== 'all' && (
                      <span className="inline-flex items-center gap-1 bg-white border border-orange-200 text-orange-700 text-xs font-medium px-2.5 py-1 rounded-full">
                        <Icon name="Tag" size={10} color="#c2410c" />
                        Status: {filters?.status}
                      </span>
                    )}
                    {filters?.rating !== 'all' && (
                      <span className="inline-flex items-center gap-1 bg-white border border-orange-200 text-orange-700 text-xs font-medium px-2.5 py-1 rounded-full">
                        <Icon name="Star" size={10} color="#c2410c" />
                        Rating: {filters?.rating}
                      </span>
                    )}
                    {filters?.minPrice && (
                      <span className="inline-flex items-center gap-1 bg-white border border-orange-200 text-orange-700 text-xs font-medium px-2.5 py-1 rounded-full">
                        <Icon name="DollarSign" size={10} color="#c2410c" />
                        Min: ${filters?.minPrice}
                      </span>
                    )}
                    {filters?.maxPrice && (
                      <span className="inline-flex items-center gap-1 bg-white border border-orange-200 text-orange-700 text-xs font-medium px-2.5 py-1 rounded-full">
                        <Icon name="DollarSign" size={10} color="#c2410c" />
                        Max: ${filters?.maxPrice}
                      </span>
                    )}
                  </div>
                </div>

                <button
                  onClick={handleClearFilters}
                  className="w-full py-3 px-6 rounded-xl text-white font-semibold text-sm transition-all hover:opacity-90 active:scale-95 shadow-md"
                  style={{ background: 'linear-gradient(to right, #F58A54, #F06F6B)' }}
                >
                  Clear All Filters
                </button>
                <button
                  onClick={() => setShowAddVendorModal(true)}
                  className="mt-3 text-sm font-medium transition-colors hover:underline"
                  style={{ color: '#F58A54' }}
                >
                  + Add a Vendor
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
      {/* Scoring Methodology Modal */}
      <ScoringMethodologyModal
        isOpen={showScoringModal}
        onClose={() => setShowScoringModal(false)} />
      {showMarkCompleteModal && (
        <MarkStackCompleteModal
          isOpen={showMarkCompleteModal}
          isSaving={savingWinner}
          quotes={quotes}
          onClose={() => setShowMarkCompleteModal(false)}
          onConfirm={async (selectedQuote) => {
            setShowMarkCompleteModal(false);
            await handleSelectQuote(selectedQuote);
            await projectService?.update(projectId, { status: 'completed' });
            trackEvent('stack_completed', {
              project_id: projectId,
              vendor_name: selectedQuote?.vendor?.name || 'unknown',
            });
            // Send stack completion email
            const { data: sessionData } = await import('../../lib/supabase')?.then(m => m?.supabase?.auth?.getSession());
            const userEmail = sessionData?.session?.user?.email;
            if (userEmail) {
              sendStackCompleteEmail(userEmail, {
                projectName: project?.name,
                selectedOption: selectedQuote?.vendor?.name,
                selectedPrice: selectedQuote?.price,
                projectCategory: project?.category,
              });
            }
            showToast(`Stack completed! ${selectedQuote?.vendor?.name} selected.`);
          }}
        />
      )}
      {showAddVendorModal && (
        <AddVendorSearchModal
          isOpen={showAddVendorModal}
          onClose={() => setShowAddVendorModal(false)}
          project={project}
          onAddVendor={(item) => {
            handleAddOnlinePriceToComparison(item);
            // If the vendor has an email, send them a quote request email automatically
            if (item?.email) {
              handleRequestQuoteFromVendor(item?.email, item?.store || item?.name);
            }
            setShowAddVendorModal(false);
          }}
        />
      )}
      {showShareModal && (
        <ShareStackModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          project={project}
          quotes={quotes}
        />
      )}
      {showRemindModal && (
        <RemindVendorsModal
          isOpen={showRemindModal}
          onClose={() => setShowRemindModal(false)}
          vendors={quotes
            ?.filter(q => (q?.status === 'pending' || q?.status === 'received') && q?.vendor?.email)
            ?.map(q => q?.vendor)}
          onSend={handleConfirmReminders}
          isSending={isSendingReminders}
        />
      )}
    </div>
  );

 };

export default ProjectComparison;